import React, { useEffect, useRef } from 'react';
import { ClientMessage, MessageStatus } from '../../../../../../store/chat/types';
import styles from './MessagesContainer.module.scss';
import ChatMessage from './ChatMessage/ChatMessage';
import ThinkingMessage from './ChatMessage/ThinkingMessage';
import { ChatRole } from '../../../../../../types/ChatRoles';

interface MessagesContainerProps {
  messages: ClientMessage[];
  awaitingResponse: boolean;
}

const MessagesContainer: React.FC<MessagesContainerProps> = ({ messages, awaitingResponse }) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom when messages change or awaiting response changes
  useEffect(() => {
    scrollToBottom();
  }, [messages, awaitingResponse]);

  // Check if we should be showing a thinking message
  const lastUserMessage = [...messages].reverse().find((message) => message.role === ChatRole.User);
  const isThinking =
    awaitingResponse &&
    messages.length > 0 &&
    lastUserMessage &&
    (lastUserMessage.status === MessageStatus.Seen || lastUserMessage.status === undefined);

  return (
    <div className={styles.messagesContainer}>
      {/* Display all regular messages */}
      {messages.map((message, index) => {
        const isLastMessage = index === messages.length - 1;

        return (
          <ChatMessage
            key={`${message.role}-${message.sequence}`}
            message={message}
            showStatus={message.role === ChatRole.User && isLastMessage}
          />
        );
      })}

      {/* Show thinking message if we're waiting for a response */}
      {isThinking && <ThinkingMessage lastUserMessageSequence={lastUserMessage.sequence} />}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessagesContainer;
